import {
  Building2,
  ChevronDown,
  ChevronRight,
  FolderKanban,
  UserRound,
  UsersRound,
} from 'lucide-react'
import type {
  OperatorProjectionPartyDto,
  OperatorTimelineDto,
} from '../../services/operatorApi'
import type { OperatorFilters } from '../../stores/operatorStore'

export const GANTT_AXIS_HEIGHT = 40
export const GANTT_ROW_HEIGHT = 52

export type GanttHierarchyRow =
  | {
      id: string
      kind: 'project'
      label: string
      meta: string
      depth: 0
    }
  | {
      id: string
      kind: 'party'
      label: string
      meta: string
      depth: 1
      partyId: string
    }
  | {
      id: string
      kind: 'team'
      label: string
      meta: string
      depth: 2
      partyId: string
      teamId: string
    }
  | {
      id: string
      kind: 'person'
      label: string
      meta: string
      depth: 3
      partyId: string
      teamId: string | null
      personId: string
    }

function partyMatches(
  party: OperatorProjectionPartyDto,
  filters: OperatorFilters,
): boolean {
  if (
    filters.organizations.length > 0
    && !filters.organizations.includes(party.id)
  ) {
    return false
  }
  if (filters.teams.length > 0) {
    const hasTeam = party.teams.some((team) => filters.teams.includes(team.id))
    if (!hasTeam) return false
  }
  if (filters.people.length > 0) {
    const people = [
      ...party.people,
      ...party.teams.flatMap((team) => team.people),
    ]
    if (!people.some((person) => filters.people.includes(person.id))) {
      return false
    }
  }
  return true
}

export function buildGanttRows(
  timeline: OperatorTimelineDto,
  projectLabel: string,
  filters: OperatorFilters,
  expandedTeamIds?: ReadonlySet<string>,
): GanttHierarchyRow[] {
  const rows: GanttHierarchyRow[] = [{
    id: `project:${timeline.projectId}`,
    kind: 'project',
    label: projectLabel,
    meta: `Revision ${timeline.revision}`,
    depth: 0,
  }]

  for (const party of timeline.hierarchy.filter((item) =>
    partyMatches(item, filters))) {
    rows.push({
      id: `party:${party.id}`,
      kind: 'party',
      label: party.name,
      meta: party.role,
      depth: 1,
      partyId: party.id,
    })

    for (const team of party.teams) {
      if (filters.teams.length > 0 && !filters.teams.includes(team.id)) continue
      const people = team.people.filter((person) =>
        filters.people.length === 0 || filters.people.includes(person.id))
      if (filters.people.length > 0 && people.length === 0) continue
      rows.push({
        id: `team:${team.id}`,
        kind: 'team',
        label: team.name,
        meta: `${people.length} ${people.length === 1 ? 'person' : 'people'}`,
        depth: 2,
        partyId: party.id,
        teamId: team.id,
      })
      const showPeople = expandedTeamIds === undefined
        || expandedTeamIds.has(team.id)
        || filters.people.length > 0
      for (const person of showPeople ? people : []) {
        rows.push({
          id: `person:${person.id}`,
          kind: 'person',
          label: person.name,
          meta: person.role ?? 'Project participant',
          depth: 3,
          partyId: party.id,
          teamId: team.id,
          personId: person.id,
        })
      }
    }

    const teamPersonIds = new Set(
      party.teams.flatMap((team) => team.people.map((person) => person.id)),
    )
    for (const person of party.people.filter((item) =>
      !teamPersonIds.has(item.id)
      && (filters.people.length === 0 || filters.people.includes(item.id)))) {
      rows.push({
        id: `person:${person.id}`,
        kind: 'person',
        label: person.name,
        meta: person.role ?? 'Project participant',
        depth: 3,
        partyId: party.id,
        teamId: null,
        personId: person.id,
      })
    }
  }

  return rows
}

const rowIcon = {
  project: FolderKanban,
  party: Building2,
  team: UsersRound,
  person: UserRound,
}

interface GanttHierarchyProps {
  rows: GanttHierarchyRow[]
  expandedTeamIds: ReadonlySet<string>
  onTeamToggle: (teamId: string) => void
}

export function GanttHierarchy({
  rows,
  expandedTeamIds,
  onTeamToggle,
}: GanttHierarchyProps) {
  return (
    <aside
      aria-label="Project communication hierarchy"
      className="border-r border-border bg-card"
    >
      <div
        className="flex items-center border-b border-border px-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground"
        style={{ height: GANTT_AXIS_HEIGHT }}
      >
        Project / organization / team / person
      </div>
      {rows.map((row) => {
        const Icon = rowIcon[row.kind]
        const className = `flex min-w-0 items-center gap-2 border-b border-border/60 px-3 ${
          row.kind === 'project'
            ? 'bg-muted/60'
            : row.kind === 'party'
              ? 'bg-muted/25'
              : 'bg-card'
        }`
        const style = {
          height: GANTT_ROW_HEIGHT,
          paddingLeft: 12 + row.depth * 15,
        }
        const content = (
          <>
            {row.kind === 'team' && (
              expandedTeamIds.has(row.teamId)
                ? <ChevronDown size={13} aria-hidden="true" />
                : <ChevronRight size={13} aria-hidden="true" />
            )}
            <span
              className={`flex size-6 shrink-0 items-center justify-center rounded-md border ${
                row.kind === 'person'
                  ? 'border-border bg-background text-muted-foreground'
                  : 'border-primary/20 bg-primary/10 text-primary'
              }`}
            >
              <Icon size={12} aria-hidden="true" />
            </span>
            <span className="min-w-0">
              <span className="block truncate text-xs font-medium text-foreground">
                {row.label}
              </span>
              <span className="block truncate text-[10px] capitalize text-muted-foreground">
                {row.meta}
              </span>
            </span>
          </>
        )
        if (row.kind === 'team') {
          const expanded = expandedTeamIds.has(row.teamId)
          return (
            <button
              key={row.id}
              type="button"
              aria-expanded={expanded}
              aria-label={`${expanded ? 'Collapse' : 'Expand'} ${row.label} team`}
              onClick={() => onTeamToggle(row.teamId)}
              className={`${className} w-full text-left outline-none hover:bg-muted/30 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/50`}
              style={style}
            >
              {content}
            </button>
          )
        }
        return (
          <div
            key={row.id}
            className={className}
            style={style}
          >
            {content}
          </div>
        )
      })}
    </aside>
  )
}
